export interface NearbyArea {
  name: string;
  slug: string;
}

export interface CityData {
  name: string;
  state: string;
  slug: string;
  distance: string;
  distanceMiles: number;
  description: string;
  heroDescription: string;
  landmarks: string[];
  localKeywords: string[];
  competitorName?: string;
  competitorSlug?: string;
  competitorCallout?: string;
  nearbyAreas: NearbyArea[];
  coordinates: {
    lat: number;
    lng: number;
  };
  seo: {
    title: string;
    description: string;
    keywords: string;
  };
  testimonial: {
    name: string;
    location: string;
    text: string;
    rating: number;
  };
  // Enhanced fields for dynamic city landing pages
  county: string;
  driveTime: string;
  population: string;
  medianIncome: string;
  topVehicleTypes: string[];
  localInsights: {
    lifestyle: string;
    popularVehicles: string;
    weatherConsiderations: string;
    localCompetitors: string[];
  };
  // NEW: Enhanced local SEO fields
  neighborhoods?: string[];
  whatBuyersWant?: string;
  vehicleRecommendations?: {
    type: string;
    reason: string;
    examples: string[];
  }[];
  localCompetitorComparison?: {
    dealerName: string;
    ourAdvantage: string;
  }[];
}

export const serviceAreaCities: CityData[] = [
  {
    name: 'Warrenton',
    state: 'Oregon',
    slug: 'warrenton',
    distance: 'Our Home Base',
    distanceMiles: 0,
    description:
      'Visit our showroom at 1425 SE 9th St for personalized service and expert guidance on all makes, models, and price points.',
    heroDescription:
      'Welcome to our home base! Visit our Warrenton showroom for face-to-face service, expert auto broker guidance, and transparent pricing on every vehicle.',
    landmarks: ['Fort Stevens State Park', 'Columbia River Maritime Museum', 'Warrenton Marina', 'Hammond Marina', 'Clatsop Spit'],
    localKeywords: [
      'Warrenton auto broker',
      'car dealer Warrenton OR',
      'used cars Warrenton',
      'auto sales Warrenton Oregon',
      'used trucks Warrenton',
      'SUVs for sale Warrenton',
      'Clatsop County auto broker',
      'cars near Fort Stevens',
    ],
    nearbyAreas: [
      { name: 'Astoria', slug: 'astoria' },
      { name: 'Seaside', slug: 'seaside' },
      { name: 'Cannon Beach', slug: 'cannon-beach' },
      { name: 'Long Beach', slug: 'long-beach-wa' },
      { name: 'Gearhart', slug: 'gearhart' },
    ],
    coordinates: { lat: 46.1651, lng: -123.9237 },
    seo: {
      title: 'Used Cars Warrenton OR | Auto Broker Showroom | Rockhound Auto',
      description:
        'Shop used cars at Rockhound Auto showroom in Warrenton, Oregon. Transparent pricing on trucks, SUVs, sedans. Better than Lum\'s Toyota or Warrenton Kia. Visit 1425 SE 9th St today.',
      keywords:
        'used cars Warrenton OR, Warrenton auto broker, car dealer Warrenton Oregon, used trucks Warrenton, SUVs Warrenton, Clatsop County auto sales, cars near Fort Stevens',
    },
    testimonial: {
      name: 'Jennifer Martinez',
      location: 'Warrenton, OR',
      text:
        'Being able to walk into the showroom and talk face-to-face made all the difference. They found me a perfect Honda CR-V at a price better than any dealership I visited.',
      rating: 5,
    },
    county: 'Clatsop County',
    driveTime: '0 minutes',
    population: '5,900',
    medianIncome: '$52,000',
    topVehicleTypes: ['SUVs', 'Trucks', 'Sedans', 'Crossovers'],
    localInsights: {
      lifestyle: 'Warrenton residents balance coastal living with practical needs—fishing, boating, and outdoor recreation are central to daily life. Vehicles need to handle wet weather and occasional beach access.',
      popularVehicles: 'Subaru Outback, Toyota Tacoma, Honda CR-V, and Ford F-150 dominate local driveways. AWD and 4WD are highly valued for Oregon Coast weather and unpaved boat launch access.',
      weatherConsiderations: 'Coastal Oregon means rain 9 months a year. Rust-resistant vehicles with good traction control are essential. Undercoating and regular washing extend vehicle life significantly.',
      localCompetitors: ['Lum\'s Toyota', 'Astoria Ford', 'Warrenton Kia'],
    },
    neighborhoods: ['Hammond', 'Downtown Warrenton', 'Skipanon Peninsula', 'Warrenton Highlands'],
    whatBuyersWant: 'Warrenton buyers want reliable vehicles that handle coastal weather without breaking the bank. Families need SUVs for beach trips and boat towing. Commercial fishermen want durable trucks. Retirees prefer comfortable sedans for Portland trips. Everyone values rust resistance and AWD for year-round safety on wet roads.',
    vehicleRecommendations: [
      {
        type: 'Best SUVs for Warrenton Families',
        reason: 'Space for beach gear, AWD for wet roads, reliable for daily use',
        examples: ['Honda CR-V', 'Toyota RAV4', 'Subaru Outback', 'Mazda CX-5'],
      },
      {
        type: 'Top Trucks for Coastal Work',
        reason: 'Towing capacity for boats, rust-resistant, durable for marina access',
        examples: ['Toyota Tacoma', 'Ford F-150', 'Chevrolet Silverado', 'Ram 1500'],
      },
      {
        type: 'Fuel-Efficient Commuters',
        reason: 'Great MPG for Portland trips, reliable, affordable maintenance',
        examples: ['Honda Civic', 'Toyota Corolla', 'Mazda3', 'Hyundai Elantra'],
      },
    ],
    localCompetitorComparison: [
      {
        dealerName: 'Lum\'s Toyota (Warrenton)',
        ourAdvantage: 'We search all makes and models statewide—not just Toyota inventory. No dealer markup, no pressure tactics. Transparent pricing from day one. Plus we deliver to your door.',
      },
      {
        dealerName: 'Warrenton Kia',
        ourAdvantage: 'We\'re not tied to one brand. We find the best vehicle for your needs and budget across Oregon. No pushy finance office, no hidden fees. Just honest car buying.',
      },
      {
        dealerName: 'Astoria Ford',
        ourAdvantage: 'We\'re not limited to Ford inventory. We find the perfect vehicle for Warrenton\'s hills and tight streets—any make, any model. Free delivery, transparent pricing, no hidden fees.',
      },
    ],
  },
  {
    name: 'Astoria',
    state: 'Oregon',
    slug: 'astoria',
    distance: '6 miles from Warrenton',
    distanceMiles: 6,
    description:
      'Astoria neighbors get the same transparent pricing and expert service—just minutes from our Warrenton showroom. Free delivery available.',
    heroDescription:
      'Astoria customers: Skip the dealership games. We bring honest pricing and expert auto broker service right to your historic riverfront community.',
    landmarks: ['Astoria Column', 'Columbia River Maritime Museum', 'Astoria-Megler Bridge', 'Flavel House', 'Riverwalk', 'Uppertown Firefighters Museum'],
    localKeywords: [
      'Astoria auto broker',
      'used cars Astoria OR',
      'car dealer Astoria Oregon',
      'Astoria vehicle delivery',
      'used trucks Astoria',
      'SUVs Astoria Oregon',
      'cars near Astoria Column',
      'Clatsop County auto sales',
    ],
    nearbyAreas: [
      { name: 'Warrenton', slug: 'warrenton' },
      { name: 'Seaside', slug: 'seaside' },
      { name: 'Cannon Beach', slug: 'cannon-beach' },
      { name: 'Gearhart', slug: 'gearhart' },
      { name: 'Long Beach', slug: 'long-beach-wa' },
    ],
    coordinates: { lat: 46.1879, lng: -123.8313 },
    seo: {
      title: 'Used Cars Astoria OR | Beat Lum\'s Toyota Prices | Rockhound Auto',
      description:
        'Shop used cars in Astoria, Oregon with free delivery. Better prices than Lum\'s Toyota or Astoria Ford. Transparent pricing on trucks, SUVs, sedans. Serving Clatsop County since day one.',
      keywords:
        'used cars Astoria OR, Astoria auto broker, car dealer Astoria Oregon, used trucks Astoria, SUVs Astoria, Clatsop County auto sales, beat Lum\'s Toyota prices',
    },
    testimonial: {
      name: 'Michael Chen',
      location: 'Astoria, OR',
      text:
        'Living in Astoria, I thought I had to deal with pushy dealerships. Rockhound Auto delivered a Toyota Tacoma to my door, saved me $2,800, and made the whole process stress-free.',
      rating: 5,
    },
    county: 'Clatsop County',
    driveTime: '10 minutes',
    population: '10,200',
    medianIncome: '$48,000',
    topVehicleTypes: ['Crossovers', 'Sedans', 'Hatchbacks', 'SUVs'],
    localInsights: {
      lifestyle: 'Astoria blends historic charm with working waterfront culture. Residents navigate steep hills, narrow streets, and frequent rain. Compact vehicles with good visibility are popular for downtown parking.',
      popularVehicles: 'Honda Fit, Subaru Crosstrek, Toyota Prius, and Mazda CX-5 are ideal for Astoria\'s hilly terrain and tight parking. Fuel efficiency matters for Portland commuters.',
      weatherConsiderations: 'Astoria averages 67 inches of rain annually. All-season tires are mandatory. Vehicles with heated seats and defrosters are highly valued. Rust prevention is critical.',
      localCompetitors: ['Astoria Ford', 'Lum\'s Toyota', 'Oceancrest Chevrolet'],
    },
    neighborhoods: ['Uppertown', 'Downtown Astoria', 'Uniontown', 'Alderbrook', 'Alameda', 'Shively'],
    whatBuyersWant: 'Astoria buyers need vehicles that handle steep hills and tight downtown parking. Young professionals want fuel-efficient cars for Portland commutes. Families need reliable SUVs for weekend adventures. Artists and creatives prefer affordable, quirky vehicles with character. Everyone values rust resistance, AWD for winter safety, and vehicles that handle sandy beach access roads.',
    vehicleRecommendations: [
      {
        type: 'Best Cars for Astoria Hills',
        reason: 'Good traction on steep grades, compact for downtown parking, reliable',
        examples: ['Subaru Crosstrek', 'Honda Fit', 'Mazda CX-5', 'Toyota Prius'],
      },
      {
        type: 'Portland Commuter Favorites',
        reason: 'Excellent fuel economy, comfortable for highway driving, low maintenance',
        examples: ['Honda Civic', 'Toyota Corolla', 'Hyundai Elantra', 'Mazda3'],
      },
      {
        type: 'Family SUVs for Coastal Living',
        reason: 'Space for kids and gear, AWD for safety, reliable for daily use',
        examples: ['Honda Pilot', 'Toyota Highlander', 'Subaru Outback', 'Mazda CX-9'],
      },
    ],
    localCompetitorComparison: [
      {
        dealerName: 'Lum\'s Toyota (Warrenton)',
        ourAdvantage: 'We deliver to Astoria for free—no need to drive to Warrenton. We search all brands, not just Toyota. Better prices because we find deals statewide. No dealer pressure or finance office games.',
      },
      {
        dealerName: 'Astoria Ford',
        ourAdvantage: 'We\'re not limited to Ford inventory. We find the perfect vehicle for Astoria\'s hills and tight streets—any make, any model. Free delivery, transparent pricing, no hidden fees.',
      },
      {
        dealerName: 'Oceancrest Chevrolet (Seaside)',
        ourAdvantage: 'We bring vehicles to Astoria—you don\'t drive to Seaside. Wider selection because we search all of Oregon. Lower overhead means better prices. Simple, honest car buying.',
      },
    ],
  },
  {
    name: 'Seaside',
    state: 'Oregon',
    slug: 'seaside',
    distance: '17 miles from Warrenton',
    distanceMiles: 17,
    description:
      'Seaside residents enjoy hassle-free auto broker service with free delivery. We understand coastal living vehicle needs.',
    heroDescription:
      'Seaside families: Get beach-ready vehicles without dealership pressure. We deliver transparent pricing and expert guidance right to the Oregon Coast.',
    landmarks: ['Seaside Promenade', 'Turnaround', 'Seaside Aquarium', 'Broadway Street', 'Seaside Civic Center', 'Necanicum Estuary'],
    localKeywords: [
      'Seaside auto broker',
      'used cars Seaside OR',
      'coastal vehicle delivery',
      'Seaside car dealer alternative',
      'used SUVs Seaside',
      'beach vehicles Seaside Oregon',
      'cars near Seaside Promenade',
      'Clatsop County auto broker',
    ],
    nearbyAreas: [
      { name: 'Cannon Beach', slug: 'cannon-beach' },
      { name: 'Astoria', slug: 'astoria' },
      { name: 'Warrenton', slug: 'warrenton' },
      { name: 'Tillamook', slug: 'tillamook' },
      { name: 'Gearhart', slug: 'gearhart' },
    ],
    coordinates: { lat: 45.9932, lng: -123.9226 },
    seo: {
      title: 'Used Cars Seaside OR | Beach-Ready Vehicles | Rockhound Auto',
      description:
        'Shop used cars in Seaside, Oregon with free delivery. Beach-ready SUVs, trucks, sedans. Better prices than Oceancrest Chevrolet or Lum\'s Toyota. Transparent pricing for coastal families.',
      keywords:
        'used cars Seaside OR, Seaside auto broker, coastal vehicle delivery, used SUVs Seaside, beach vehicles Oregon Coast, Clatsop County auto sales, beat Oceancrest Chevrolet',
    },
    testimonial: {
      name: 'Sarah Thompson',
      location: 'Seaside, OR',
      text:
        'As a Seaside local, I wanted a reliable SUV for beach trips. Rockhound Auto found me a perfect Honda Pilot, beat local dealer prices by $3,200, and delivered it to my driveway.',
      rating: 5,
    },
    county: 'Clatsop County',
    driveTime: '20 minutes',
    population: '6,800',
    medianIncome: '$45,000',
    topVehicleTypes: ['SUVs', 'Crossovers', 'Minivans', 'Trucks'],
    localInsights: {
      lifestyle: 'Seaside is Oregon\'s classic beach town—tourism, hospitality, and family recreation define daily life. Residents need vehicles that handle beach access, carry surfboards and bikes, and withstand salt air. Summer crowds mean parking challenges.',
      popularVehicles: 'Honda Pilot, Toyota Highlander, Subaru Outback, and Ford Explorer are perfect for Seaside families. Roof racks for beach gear, AWD for winter storms, and space for kids are essential.',
      weatherConsiderations: 'Salt air accelerates rust—regular washing and undercoating are critical. Winter storms bring heavy rain and wind. AWD provides peace of mind. Heated seats and defrosters are must-haves for foggy mornings.',
      localCompetitors: ['Oceancrest Chevrolet', 'Lum\'s Toyota', 'Astoria Ford'],
    },
    neighborhoods: ['Downtown Seaside', 'Gearhart', 'Necanicum', 'South Seaside', 'The Cove'],
    whatBuyersWant: 'Seaside buyers want family-friendly vehicles with space for beach gear, bikes, and surfboards. Tourism workers need reliable commuters. Vacation rental owners want durable vehicles for property management. Retirees prefer comfortable SUVs for exploring the coast. Everyone values rust resistance, AWD for winter safety, and vehicles that handle sandy beach access roads.',
    vehicleRecommendations: [
      {
        type: 'Best Family SUVs for Beach Life',
        reason: 'Three rows for kids, roof racks for gear, AWD for safety, rust-resistant',
        examples: ['Honda Pilot', 'Toyota Highlander', 'Subaru Ascent', 'Ford Explorer'],
      },
      {
        type: 'Perfect Beach Crossovers',
        reason: 'Compact for downtown parking, AWD for beach access, fuel-efficient',
        examples: ['Subaru Outback', 'Honda CR-V', 'Toyota RAV4', 'Mazda CX-5'],
      },
      {
        type: 'Reliable Work Vehicles',
        reason: 'Durable for property management, space for tools, affordable maintenance',
        examples: ['Toyota Tacoma', 'Ford Ranger', 'Honda Ridgeline', 'Chevrolet Colorado'],
      },
    ],
    localCompetitorComparison: [
      {
        dealerName: 'Oceancrest Chevrolet (Seaside)',
        ourAdvantage: 'We search all brands—not just Chevrolet. Better selection, better prices. We deliver to your Seaside home for free. No dealer pressure, no finance office games. Just honest car buying.',
      },
      {
        dealerName: 'Lum\'s Toyota (Warrenton)',
        ourAdvantage: 'We bring vehicles to Seaside—you don\'t drive to Warrenton. We find the best vehicle for your needs across all brands. Lower prices because we search statewide. Transparent pricing from day one.',
      },
      {
        dealerName: 'Astoria Ford',
        ourAdvantage: 'We\'re not limited to Ford inventory. We find beach-ready vehicles perfect for Seaside families—any make, any model. Free delivery, transparent pricing, no hidden fees.',
      },
    ],
  },
  {
    name: 'Portland',
    state: 'Oregon',
    slug: 'portland',
    distance: '95 miles from Warrenton',
    distanceMiles: 95,
    description:
      'Portland metro customers get expert auto broker service with coordinated delivery. We find better deals than Portland dealerships.',
    heroDescription:
      'Portland residents: Skip the dealership markup. We search Oregon\'s entire inventory and coordinate delivery right to your neighborhood.',
    landmarks: ['Powell\'s Books', 'Washington Park', 'Pearl District', 'Hawthorne District', 'Forest Park', 'Tom McCall Waterfront Park'],
    localKeywords: [
      'Portland auto broker',
      'used cars Portland OR',
      'Portland metro delivery',
      'Multnomah County auto sales',
      'used cars Pearl District',
      'SUVs Portland Oregon',
      'electric cars Portland',
      'hybrid vehicles Portland',
    ],
    nearbyAreas: [
      { name: 'Beaverton', slug: 'beaverton' },
      { name: 'Gresham', slug: 'gresham' },
      { name: 'Vancouver', slug: 'vancouver' },
      { name: 'Hillsboro', slug: 'hillsboro' },
      { name: 'Lake Oswego', slug: 'lake-oswego' },
      { name: 'Tigard', slug: 'tigard' },
    ],
    coordinates: { lat: 45.5152, lng: -122.6784 },
    seo: {
      title: 'Used Cars Portland OR | Beat Dealership Prices | Rockhound Auto',
      description:
        'Shop used cars in Portland, Oregon with delivery. Better prices than Portland dealerships. Transparent pricing on hybrids, EVs, SUVs, trucks. Serving Multnomah County metro area.',
      keywords:
        'used cars Portland OR, Portland auto broker, Portland metro delivery, Multnomah County auto sales, hybrid cars Portland, electric vehicles Portland, beat dealership prices',
    },
    testimonial: {
      name: 'Kevin Nguyen',
      location: 'Portland, OR',
      text:
        'As a Portland resident, I thought I had to deal with pushy dealerships. Rockhound Auto found me a Tesla Model 3, saved me $4,200, and delivered it to my SE Portland home.',
      rating: 5,
    },
    county: 'Multnomah County',
    driveTime: '1 hour 45 minutes',
    population: '652,000',
    medianIncome: '$71,000',
    topVehicleTypes: ['Hybrids', 'Electric Vehicles', 'Crossovers', 'Sedans'],
    localInsights: {
      lifestyle: 'Portland residents value sustainability, outdoor recreation, and urban convenience. Bike culture is strong, but cars are essential for mountain trips and coast visits. Eco-friendly vehicles are highly desirable. Traffic congestion makes fuel efficiency important.',
      popularVehicles: 'Toyota Prius, Tesla Model 3, Subaru Outback, Honda CR-V, and Nissan Leaf dominate Portland driveways. Hybrids and EVs are status symbols. AWD is valued for Cascade mountain trips.',
      weatherConsiderations: 'Portland gets 43 inches of rain annually. All-season tires are standard. Heated seats and defrosters are appreciated. Rust is less severe than the coast but still a concern for older vehicles.',
      localCompetitors: ['Dick Hannah Dealerships', 'Ron Tonkin', 'Tonkin', 'Carr Automotive Group'],
    },
    neighborhoods: ['Pearl District', 'Hawthorne', 'Alberta Arts', 'Division', 'Sellwood', 'St. Johns', 'Eastmoreland', 'Northwest Portland', 'Belmont'],
    whatBuyersWant: 'Portland buyers want eco-friendly vehicles—hybrids, plug-in hybrids, and EVs are top priorities. Young professionals need fuel-efficient commuters for traffic. Families want crossovers with space for bikes and camping gear. Outdoor enthusiasts need AWD for mountain trips. Everyone values low emissions, good MPG, and vehicles that reflect Portland\'s environmental values.',
    vehicleRecommendations: [
      {
        type: 'Best Eco-Friendly Vehicles',
        reason: 'Low emissions, excellent MPG, Portland values, HOV lane access',
        examples: ['Toyota Prius', 'Honda Insight', 'Nissan Leaf', 'Chevrolet Volt'],
      },
      {
        type: 'Top Electric Vehicles',
        reason: 'Zero emissions, low operating costs, Portland charging infrastructure',
        examples: ['Tesla Model 3', 'Nissan Leaf', 'Chevrolet Bolt', 'Hyundai Kona Electric'],
      },
      {
        type: 'Adventure-Ready Crossovers',
        reason: 'AWD for mountains, space for bikes and gear, fuel-efficient',
        examples: ['Subaru Outback', 'Honda CR-V', 'Toyota RAV4 Hybrid', 'Mazda CX-5'],
      },
    ],
    localCompetitorComparison: [
      {
        dealerName: 'Dick Hannah Dealerships',
        ourAdvantage: 'We search all brands statewide—not just their inventory. No dealer markup, no pressure tactics. We deliver to your Portland neighborhood. Transparent pricing beats their "market adjustments" every time.',
      },
      {
        dealerName: 'Ron Tonkin / Tonkin Dealerships',
        ourAdvantage: 'We\'re not tied to one brand or location. We find the best vehicle for your needs across Oregon. Lower overhead means better prices. No pushy finance office. Just honest car buying.',
      },
      {
        dealerName: 'Carr Automotive Group',
        ourAdvantage: 'We bring vehicles to you—no need to visit multiple dealerships. Better selection because we search statewide. Lower prices because we skip dealer overhead. Simple, transparent pricing.',
      },
    ],
  },
  {
    name: 'Beaverton',
    state: 'Oregon',
    slug: 'beaverton',
    distance: '88 miles from Warrenton',
    distanceMiles: 88,
    description:
      'Beaverton families get transparent auto broker service with metro delivery. We find better deals than Washington County dealerships.',
    heroDescription:
      'Beaverton customers: Get honest pricing without dealership games. We coordinate delivery throughout Washington County.',
    landmarks: ['Nike World Headquarters', 'Tualatin Hills Nature Park', 'Beaverton Town Square', 'Cedar Hills Crossing'],
    localKeywords: [
      'Beaverton auto broker',
      'used cars Beaverton OR',
      'Washington County delivery',
      'Beaverton car dealer alternative',
    ],
    nearbyAreas: [
      { name: 'Hillsboro', slug: 'hillsboro' },
      { name: 'Tigard', slug: 'tigard' },
      { name: 'Portland', slug: 'portland' },
      { name: 'Lake Oswego', slug: 'lake-oswego' },
      { name: 'Forest Grove', slug: 'forest-grove' },
    ],
    coordinates: { lat: 45.4871, lng: -122.8037 },
    seo: {
      title: 'Beaverton OR Auto Broker | Washington County Delivery',
      description:
        'Beaverton gets transparent auto broker service with metro delivery. Better deals than Washington County dealerships—statewide search, honest pricing from Warrenton.',
      keywords:
        'Beaverton auto broker, used cars Beaverton OR, Washington County delivery, Beaverton car dealer, Portland metro auto sales',
    },
    testimonial: {
      name: 'Michelle Park',
      location: 'Beaverton, OR',
      text:
        'Rockhound Auto made car buying stress-free for our Beaverton family. They found a Honda Odyssey perfect for our kids, beat local dealer prices by $3,100, and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Hillsboro',
    state: 'Oregon',
    slug: 'hillsboro',
    distance: '82 miles from Warrenton',
    distanceMiles: 82,
    description:
      'Hillsboro tech workers get expert auto broker service with coordinated delivery. We understand commuter vehicle needs.',
    heroDescription:
      'Hillsboro residents: Get reliable commuter vehicles without dealership pressure. We coordinate delivery throughout Washington County.',
    landmarks: ['Intel Campus', 'Orenco Station', 'Jackson Bottom Wetlands', 'Hillsboro Stadium'],
    localKeywords: [
      'Hillsboro auto broker',
      'used cars Hillsboro OR',
      'tech corridor vehicle delivery',
      'Washington County auto sales',
    ],
    nearbyAreas: [
      { name: 'Beaverton', slug: 'beaverton' },
      { name: 'Forest Grove', slug: 'forest-grove' },
      { name: 'Portland', slug: 'portland' },
      { name: 'Tigard', slug: 'tigard' },
    ],
    coordinates: { lat: 45.5229, lng: -122.9898 },
    seo: {
      title: 'Hillsboro OR Auto Broker | Tech Corridor Vehicle Delivery',
      description:
        'Hillsboro gets expert auto broker service with delivery. Reliable commuter vehicles, transparent pricing—better than Washington County dealerships. Serving tech corridor workers.',
      keywords:
        'Hillsboro auto broker, used cars Hillsboro OR, tech corridor vehicle delivery, Washington County auto sales, Hillsboro car dealer',
    },
    testimonial: {
      name: 'Daniel Lee',
      location: 'Hillsboro, OR',
      text:
        'Working at Intel, I needed a reliable commuter car. Rockhound Auto found me a Toyota Prius in perfect condition, saved me $2,300, and delivered it to my Orenco Station apartment.',
      rating: 5,
    },
  },
  {
    name: 'Forest Grove',
    state: 'Oregon',
    slug: 'forest-grove',
    distance: '75 miles from Warrenton',
    distanceMiles: 75,
    description:
      'Forest Grove families get transparent auto broker service with free delivery. We understand rural Washington County vehicle needs.',
    heroDescription:
      'Forest Grove residents: Get reliable vehicles without traveling to Portland. We deliver transparent pricing and expert guidance right to your community.',
    landmarks: ['Pacific University', 'Valley Art Gallery', 'Fernhill Wetlands', 'Historic Downtown'],
    localKeywords: [
      'Forest Grove auto broker',
      'used cars Forest Grove OR',
      'rural Washington County delivery',
      'Forest Grove car dealer',
    ],
    nearbyAreas: [
      { name: 'Hillsboro', slug: 'hillsboro' },
      { name: 'Beaverton', slug: 'beaverton' },
      { name: 'Portland', slug: 'portland' },
      { name: 'Cornelius', slug: 'cornelius' },
    ],
    coordinates: { lat: 45.5198, lng: -123.1107 },
    seo: {
      title: 'Forest Grove OR Auto Broker | Free Delivery | Rockhound Auto',
      description:
        'Forest Grove gets transparent auto broker service with free delivery. Reliable vehicles for rural Washington County—better pricing than Portland dealerships.',
      keywords:
        'Forest Grove auto broker, used cars Forest Grove OR, rural Washington County delivery, Forest Grove car dealer, Pacific University auto sales',
    },
    testimonial: {
      name: 'Rachel Green',
      location: 'Forest Grove, OR',
      text:
        'Rockhound Auto found us a Ford Explorer perfect for our Forest Grove family. They delivered it to our home, beat Portland dealer prices by $2,900, and made everything easy.',
      rating: 5,
    },
  },
  {
    name: 'Tigard',
    state: 'Oregon',
    slug: 'tigard',
    distance: '92 miles from Warrenton',
    distanceMiles: 92,
    description:
      'Tigard customers get expert auto broker service with metro delivery. We find better deals than Washington County dealerships.',
    heroDescription:
      'Tigard families: Skip the dealership runaround. We coordinate delivery throughout Washington County with transparent pricing.',
    landmarks: ['Bridgeport Village', 'Cook Park', 'Tigard Triangle', 'Fanno Creek Trail'],
    localKeywords: [
      'Tigard auto broker',
      'used cars Tigard OR',
      'Washington County metro delivery',
      'Tigard car dealer alternative',
    ],
    nearbyAreas: [
      { name: 'Beaverton', slug: 'beaverton' },
      { name: 'Lake Oswego', slug: 'lake-oswego' },
      { name: 'Portland', slug: 'portland' },
      { name: 'Tualatin', slug: 'tualatin' },
    ],
    coordinates: { lat: 45.4312, lng: -122.7715 },
    seo: {
      title: 'Tigard OR Auto Broker | Metro Delivery | Rockhound Auto',
      description:
        'Tigard gets expert auto broker service with metro delivery. Better deals than Washington County dealerships—statewide search, transparent pricing from Warrenton.',
      keywords:
        'Tigard auto broker, used cars Tigard OR, Washington County metro delivery, Tigard car dealer, Portland metro auto sales',
    },
    testimonial: {
      name: 'Brian Walsh',
      location: 'Tigard, OR',
      text:
        'Rockhound Auto made buying a car easy for our Tigard family. They found a Mazda CX-5 in perfect condition, beat local dealer prices by $2,600, and delivered it to our driveway.',
      rating: 5,
    },
  },
  {
    name: 'Lake Oswego',
    state: 'Oregon',
    slug: 'lake-oswego',
    distance: '98 miles from Warrenton',
    distanceMiles: 98,
    description:
      'Lake Oswego residents get premium auto broker service with coordinated delivery. We find luxury and premium vehicles at transparent prices.',
    heroDescription:
      'Lake Oswego customers: Get premium vehicles without premium dealership markups. We coordinate delivery throughout Clackamas County.',
    landmarks: ['Oswego Lake', 'George Rogers Park', 'Lake Oswego Farmers Market', 'Millennium Plaza Park'],
    localKeywords: [
      'Lake Oswego auto broker',
      'luxury cars Lake Oswego',
      'premium vehicle delivery OR',
      'Clackamas County auto sales',
    ],
    nearbyAreas: [
      { name: 'Tigard', slug: 'tigard' },
      { name: 'Portland', slug: 'portland' },
      { name: 'Oregon City', slug: 'oregon-city' },
      { name: 'West Linn', slug: 'west-linn' },
    ],
    coordinates: { lat: 45.4207, lng: -122.6706 },
    seo: {
      title: 'Lake Oswego OR Auto Broker | Premium Vehicle Delivery',
      description:
        'Lake Oswego gets premium auto broker service with delivery. Luxury vehicles at transparent prices—better than Portland dealerships. Serving Clackamas County.',
      keywords:
        'Lake Oswego auto broker, luxury cars Lake Oswego OR, premium vehicle delivery, Clackamas County auto sales, Lake Oswego car dealer',
    },
    testimonial: {
      name: 'Catherine Bennett',
      location: 'Lake Oswego, OR',
      text:
        'Rockhound Auto found me a BMW X5 in pristine condition. They coordinated delivery to Lake Oswego, saved me $5,200 vs local luxury dealers, and provided exceptional service.',
      rating: 5,
    },
  },
  {
    name: 'Oregon City',
    state: 'Oregon',
    slug: 'oregon-city',
    distance: '105 miles from Warrenton',
    distanceMiles: 105,
    description:
      'Oregon City families get transparent auto broker service with coordinated delivery. We understand Clackamas County vehicle needs.',
    heroDescription:
      'Oregon City residents: Get reliable vehicles without dealership pressure. We coordinate delivery throughout Clackamas County.',
    landmarks: ['Willamette Falls', 'End of the Oregon Trail', 'McLoughlin House', 'Oregon City Municipal Elevator'],
    localKeywords: [
      'Oregon City auto broker',
      'used cars Oregon City',
      'Clackamas County delivery',
      'Oregon City car dealer',
    ],
    nearbyAreas: [
      { name: 'Lake Oswego', slug: 'lake-oswego' },
      { name: 'Milwaukie', slug: 'milwaukie' },
      { name: 'Portland', slug: 'portland' },
      { name: 'Gladstone', slug: 'gladstone' },
    ],
    coordinates: { lat: 45.4462, lng: -122.6395 },
    seo: {
      title: 'Oregon City Auto Broker | Clackamas County Delivery',
      description:
        'Oregon City gets transparent auto broker service with delivery. Reliable vehicles for Clackamas County families—better pricing than Portland dealerships.',
      keywords:
        'Oregon City auto broker, used cars Oregon City, Clackamas County delivery, Oregon City car dealer, historic Oregon auto sales',
    },
    testimonial: {
      name: 'Mark Johnson',
      location: 'Oregon City, OR',
      text:
        'Rockhound Auto found us a Honda CR-V perfect for our Oregon City family. They coordinated delivery, beat local dealer prices by $2,800, and made the process incredibly smooth.',
      rating: 5,
    },
  },
  {
    name: 'Gresham',
    state: 'Oregon',
    slug: 'gresham',
    distance: '102 miles from Warrenton',
    distanceMiles: 102,
    description:
      'Gresham customers get expert auto broker service with metro delivery. We find better deals than Multnomah County dealerships.',
    heroDescription:
      'Gresham families: Get honest pricing without dealership games. We coordinate delivery throughout East Portland metro.',
    landmarks: ['Mt. Hood Community College', 'Gresham Station', 'Main City Park', 'Powell Butte Nature Park'],
    localKeywords: [
      'Gresham auto broker',
      'used cars Gresham OR',
      'East Portland delivery',
      'Multnomah County auto sales',
    ],
    nearbyAreas: [
      { name: 'Portland', slug: 'portland' },
      { name: 'Milwaukie', slug: 'milwaukie' },
      { name: 'Troutdale', slug: 'troutdale' },
      { name: 'Wood Village', slug: 'wood-village' },
    ],
    coordinates: { lat: 45.4982, lng: -122.4302 },
    seo: {
      title: 'Gresham OR Auto Broker | East Metro Delivery | Rockhound Auto',
      description:
        'Gresham gets expert auto broker service with metro delivery. Better deals than Multnomah County dealerships—statewide search, transparent pricing from Warrenton.',
      keywords:
        'Gresham auto broker, used cars Gresham OR, East Portland delivery, Multnomah County auto sales, Gresham car dealer',
    },
    testimonial: {
      name: 'Angela Torres',
      location: 'Gresham, OR',
      text:
        'Rockhound Auto made car buying easy for our Gresham family. They found a Toyota Highlander in excellent condition, beat local prices by $3,300, and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Milwaukie',
    state: 'Oregon',
    slug: 'milwaukie',
    distance: '100 miles from Warrenton',
    distanceMiles: 100,
    description:
      'Milwaukie residents get transparent auto broker service with metro delivery. We understand Clackamas County commuter needs.',
    heroDescription:
      'Milwaukie customers: Get reliable commuter vehicles without dealership pressure. We coordinate delivery throughout Clackamas County.',
    landmarks: ['Milwaukie Farmers Market', 'Elk Rock Island', 'Downtown Milwaukie', 'Kellogg Lake'],
    localKeywords: [
      'Milwaukie auto broker',
      'used cars Milwaukie OR',
      'Clackamas County metro delivery',
      'Milwaukie car dealer',
    ],
    nearbyAreas: [
      { name: 'Portland', slug: 'portland' },
      { name: 'Oregon City', slug: 'oregon-city' },
      { name: 'Gresham', slug: 'gresham' },
      { name: 'Lake Oswego', slug: 'lake-oswego' },
    ],
    coordinates: { lat: 45.4462, lng: -122.6395 },
    seo: {
      title: 'Milwaukie OR Auto Broker | Metro Delivery | Rockhound Auto',
      description:
        'Milwaukie gets transparent auto broker service with metro delivery. Reliable commuter vehicles for Clackamas County—better than Portland dealerships.',
      keywords:
        'Milwaukie auto broker, used cars Milwaukie OR, Clackamas County metro delivery, Milwaukie car dealer, Portland metro auto sales',
    },
    testimonial: {
      name: 'Steven Martinez',
      location: 'Milwaukie, OR',
      text:
        'Rockhound Auto found me a Nissan Altima perfect for commuting to Portland. They delivered it to Milwaukie, saved me $2,200, and made the entire process hassle-free.',
      rating: 5,
    },
  },
  {
    name: 'Salem',
    state: 'Oregon',
    slug: 'salem',
    distance: '120 miles from Warrenton',
    distanceMiles: 120,
    description:
      'Salem customers get expert auto broker service with coordinated delivery. We find better deals than Marion County dealerships.',
    heroDescription:
      'Salem residents: Skip the dealership markup. We search Oregon\'s entire inventory and coordinate delivery to the capital city.',
    landmarks: ['Oregon State Capitol', 'Willamette University', 'Bush\'s Pasture Park', 'Riverfront Park'],
    localKeywords: [
      'Salem auto broker',
      'used cars Salem OR',
      'Marion County delivery',
      'Salem car dealer alternative',
    ],
    nearbyAreas: [
      { name: 'Albany', slug: 'albany' },
      { name: 'Corvallis', slug: 'corvallis' },
      { name: 'Eugene', slug: 'eugene' },
      { name: 'Keizer', slug: 'keizer' },
    ],
    coordinates: { lat: 44.9429, lng: -123.0351 },
    seo: {
      title: 'Salem OR Auto Broker | Capital City Vehicle Delivery',
      description:
        'Salem gets expert auto broker service with delivery. Better deals than Marion County dealerships—statewide search, transparent pricing from Warrenton.',
      keywords:
        'Salem auto broker, used cars Salem OR, Marion County delivery, Salem car dealer, Oregon capital city auto sales',
    },
    testimonial: {
      name: 'Jennifer Davis',
      location: 'Salem, OR',
      text:
        'Working for the state, I needed a reliable commuter car. Rockhound Auto found me a Honda Accord in perfect condition, coordinated delivery to Salem, and saved me $2,700.',
      rating: 5,
    },
  },
  {
    name: 'Albany',
    state: 'Oregon',
    slug: 'albany',
    distance: '145 miles from Warrenton',
    distanceMiles: 145,
    description:
      'Albany families get transparent auto broker service with coordinated delivery. We understand Linn County vehicle needs.',
    heroDescription:
      'Albany residents: Get reliable vehicles without traveling to Salem or Eugene. We coordinate delivery throughout the mid-Willamette Valley.',
    landmarks: ['Historic Downtown Albany', 'Monteith Riverpark', 'Albany Carousel', 'Timber Linn Memorial Park'],
    localKeywords: [
      'Albany auto broker',
      'used cars Albany OR',
      'Linn County delivery',
      'mid-Willamette Valley auto sales',
    ],
    nearbyAreas: [
      { name: 'Corvallis', slug: 'corvallis' },
      { name: 'Salem', slug: 'salem' },
      { name: 'Eugene', slug: 'eugene' },
      { name: 'Lebanon', slug: 'lebanon' },
    ],
    coordinates: { lat: 44.6365, lng: -123.1059 },
    seo: {
      title: 'Albany OR Auto Broker | Mid-Valley Delivery | Rockhound Auto',
      description:
        'Albany gets transparent auto broker service with delivery. Reliable vehicles for Linn County families—better pricing than Salem or Eugene dealerships.',
      keywords:
        'Albany auto broker, used cars Albany OR, Linn County delivery, mid-Willamette Valley auto sales, Albany car dealer',
    },
    testimonial: {
      name: 'Thomas Wright',
      location: 'Albany, OR',
      text:
        'Rockhound Auto found us a Chevy Equinox perfect for our Albany family. They coordinated delivery, beat local dealer prices by $2,500, and provided excellent service.',
      rating: 5,
    },
  },
  {
    name: 'Corvallis',
    state: 'Oregon',
    slug: 'corvallis',
    distance: '130 miles from Warrenton',
    distanceMiles: 130,
    description:
      'Corvallis customers get expert auto broker service with coordinated delivery. We find better deals than Lane County dealerships.',
    heroDescription:
      'Corvallis residents: Get reliable vehicles without dealership pressure. We coordinate delivery throughout Benton County.',
    landmarks: ['Oregon State University', 'Avery Park', 'Majestic Theatre', 'Peavy Arboretum'],
    localKeywords: [
      'Corvallis auto broker',
      'used cars Corvallis OR',
      'Benton County delivery',
      'OSU student vehicles',
    ],
    nearbyAreas: [
      { name: 'Albany', slug: 'albany' },
      { name: 'Salem', slug: 'salem' },
      { name: 'Eugene', slug: 'eugene' },
      { name: 'Newport', slug: 'newport' },
    ],
    coordinates: { lat: 44.0521, lng: -123.0868 },
    seo: {
      title: 'Corvallis OR Auto Broker | OSU Student & Family Vehicles',
      description:
        'Corvallis gets expert auto broker service with delivery. Reliable vehicles for Benton County students and families—transparent pricing from Warrenton.',
      keywords:
        'Corvallis auto broker, used cars Corvallis OR, Benton County delivery, OSU student vehicles, Corvallis car dealer',
    },
    testimonial: {
      name: 'Sarah Mitchell',
      location: 'Corvallis, OR',
      text:
        'As an OSU grad student, I needed an affordable reliable car. Rockhound Auto found me a Toyota Corolla in great condition, coordinated delivery to Corvallis, and saved me $1,900.',
      rating: 5,
    },
  },
  {
    name: 'Eugene',
    state: 'Oregon',
    slug: 'eugene',
    distance: '165 miles from Warrenton',
    distanceMiles: 165,
    description:
      'Eugene customers get expert auto broker service with coordinated delivery. We find better deals than Lane County dealerships.',
    heroDescription:
      'Eugene residents: Skip the dealership runaround. We search Oregon\'s entire inventory and coordinate delivery to the Emerald City.',
    landmarks: ['University of Oregon', 'Hayward Field', 'Skinner Butte', 'Saturday Market'],
    localKeywords: [
      'Eugene auto broker',
      'used cars Eugene OR',
      'Lane County delivery',
      'Eugene car dealer alternative',
    ],
    nearbyAreas: [
      { name: 'Springfield', slug: 'springfield' },
      { name: 'Corvallis', slug: 'corvallis' },
      { name: 'Newport', slug: 'newport' },
      { name: 'Cottage Grove', slug: 'cottage-grove' },
    ],
    coordinates: { lat: 44.0521, lng: -123.0868 },
    seo: {
      title: 'Eugene OR Auto Broker | Beat Dealership Prices',
      description:
        'Eugene gets expert auto broker service with delivery. We find better deals than Lane County dealerships—statewide search, transparent pricing from Warrenton.',
      keywords:
        'Eugene auto broker, used cars Eugene OR, Lane County delivery, Eugene car dealer, University of Oregon auto sales',
    },
    testimonial: {
      name: 'Michael Brown',
      location: 'Eugene, OR',
      text:
        'Rockhound Auto made buying a car easy for our Eugene family. They found a Subaru Outback perfect for Oregon adventures, coordinated delivery, and saved us $3,100.',
      rating: 5,
    },
  },
  {
    name: 'Springfield',
    state: 'Oregon',
    slug: 'springfield',
    distance: '170 miles from Warrenton',
    distanceMiles: 170,
    description:
      'Springfield families get transparent auto broker service with coordinated delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'Springfield residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout Grays Harbor County.',
    landmarks: ['Gateway Mall', 'Dorris Ranch', 'Island Park', 'McKenzie River'],
    localKeywords: [
      'Springfield auto broker',
      'used cars Springfield OR',
      'Grays Harbor delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Eugene', slug: 'eugene' },
      { name: 'Corvallis', slug: 'corvallis' },
      { name: 'Albany', slug: 'albany' },
      { name: 'Cottage Grove', slug: 'cottage-grove' },
    ],
    coordinates: { lat: 44.0468, lng: -123.0832 },
    seo: {
      title: 'Springfield OR Auto Broker | Lane County Delivery',
      description:
        'Springfield gets transparent auto broker service with delivery. Reliable vehicles for Lane County families—better pricing than Eugene dealerships.',
      keywords:
        'Springfield auto broker, used cars Springfield OR, Lane County delivery, Springfield car dealer, Eugene metro auto sales',
    },
    testimonial: {
      name: 'Patricia Garcia',
      location: 'Springfield, OR',
      text:
        'Rockhound Auto found us a Ford Escape perfect for our Springfield family. They coordinated delivery, beat local dealer prices by $2,400, and made everything easy.',
      rating: 5,
    },
  },
  {
    name: 'Cottage Grove',
    state: 'Oregon',
    slug: 'cottage-grove',
    distance: '155 miles from Warrenton',
    distanceMiles: 155,
    description:
      'Cottage Grove customers get expert auto broker service with coordinated delivery. We understand Lane County vehicle needs.',
    heroDescription:
      'Cottage Grove residents: Get reliable vehicles without dealership pressure. We coordinate delivery throughout Lane County.',
    landmarks: ['Cottage Grove State Park', 'Cottage Grove Museum', 'Cottage Grove Golf Course', 'Cottage Grove Cemetery'],
    localKeywords: [
      'Cottage Grove auto broker',
      'used cars Cottage Grove OR',
      'Lane County delivery',
      'Cottage Grove car dealer',
    ],
    nearbyAreas: [
      { name: 'Springfield', slug: 'springfield' },
      { name: 'Eugene', slug: 'eugene' },
      { name: 'Albany', slug: 'albany' },
      { name: 'Cottage Grove', slug: 'cottage-grove' },
    ],
    coordinates: { lat: 44.0468, lng: -123.0832 },
    seo: {
      title: 'Cottage Grove OR Auto Broker | Lane County Delivery',
      description:
        'Cottage Grove gets expert auto broker service with delivery. Reliable vehicles for Lane County families—better pricing than Eugene dealerships.',
      keywords:
        'Cottage Grove auto broker, used cars Cottage Grove OR, Lane County delivery, Cottage Grove car dealer, Eugene metro auto sales',
    },
    testimonial: {
      name: 'James Wilson',
      location: 'Cottage Grove, OR',
      text:
        'Rockhound Auto made buying a car easy for our Cottage Grove family. They found a Hyundai Elantra perfect for our family, beat local dealer prices by $2,100, and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Vancouver',
    state: 'Washington',
    slug: 'vancouver',
    distance: '85 miles from Warrenton',
    distanceMiles: 85,
    description:
      'Vancouver customers get expert auto broker service with coordinated delivery. We help Washington residents avoid sales tax on Oregon purchases.',
    heroDescription:
      'Vancouver residents: Buy from Oregon and save on sales tax. We coordinate delivery to the Bay Area with transparent pricing.',
    landmarks: ['Fort Vancouver', 'Waterfront Park', 'Esther Short Park', 'Pearson Field'],
    localKeywords: [
      'Vancouver auto broker',
      'used cars Vancouver WA',
      'Clark County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Portland', slug: 'portland' },
      { name: 'Camas', slug: 'camas' },
      { name: 'Battle Ground', slug: 'battle-ground' },
      { name: 'Ridgefield', slug: 'ridgefield' },
      { name: 'Washougal', slug: 'washougal' },
    ],
    coordinates: { lat: 45.6387, lng: -122.6615 },
    seo: {
      title: 'Vancouver WA Auto Broker | Oregon Sales Tax Savings',
      description:
        'Vancouver gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—transparent pricing for Clark County. Better than WA dealerships.',
      keywords:
        'Vancouver auto broker, used cars Vancouver WA, Clark County delivery, Oregon sales tax savings, Vancouver car dealer',
    },
    testimonial: {
      name: 'Christopher Lee',
      location: 'Vancouver, WA',
      text:
        'Living in Vancouver, buying from Oregon saved me thousands in sales tax. Rockhound Auto found a perfect Jeep Grand Cherokee, coordinated delivery, and made it seamless.',
      rating: 5,
    },
  },
  {
    name: 'Camas',
    state: 'Washington',
    slug: 'camas',
    distance: '95 miles from Warrenton',
    distanceMiles: 95,
    description:
      'Camas residents get transparent auto broker service with coordinated delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'Camas families: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout East Clark County.',
    landmarks: ['Lacamas Lake', 'Downtown Camas', 'Crown Park', 'Camas Meadows Golf Club'],
    localKeywords: [
      'Camas auto broker',
      'used cars Camas WA',
      'East Clark County delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Washougal', slug: 'washougal' },
      { name: 'Vancouver', slug: 'vancouver' },
      { name: 'Portland', slug: 'portland' },
      { name: 'Gresham', slug: 'gresham' },
    ],
    coordinates: { lat: 45.5871, lng: -122.3995 },
    seo: {
      title: 'Camas WA Auto Broker | Oregon Tax-Free Vehicle Delivery',
      description:
        'Camas gets transparent auto broker service with delivery. Buy from Oregon, save on sales tax—expert guidance for East Clark County families.',
      keywords:
        'Camas auto broker, used cars Camas WA, East Clark County delivery, Oregon tax-free vehicles, Camas car dealer',
    },
    testimonial: {
      name: 'Nicole Peterson',
      location: 'Camas, WA',
      text:
        'Rockhound Auto helped us buy from Oregon and save thousands on sales tax. They found a perfect Honda Pilot and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Washougal',
    state: 'Washington',
    slug: 'washougal',
    distance: '100 miles from Warrenton',
    distanceMiles: 100,
    description:
      'Washougal customers get expert auto broker service with coordinated delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Washougal residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout the Columbia River Gorge.',
    landmarks: ['Washougal River', 'Steamboat Landing', 'Cape Horn Trail', 'Pendleton Woolen Mills'],
    localKeywords: [
      'Washougal auto broker',
      'used cars Washougal WA',
      'Columbia Gorge delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Camas', slug: 'camas' },
      { name: 'Vancouver', slug: 'vancouver' },
      { name: 'Portland', slug: 'portland' },
      { name: 'Hood River', slug: 'hood-river' },
    ],
    coordinates: { lat: 45.5826, lng: -122.3534 },
    seo: {
      title: 'Washougal WA Auto Broker | Gorge Vehicle Delivery',
      description:
        'Washougal gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—transparent pricing for Columbia Gorge communities.',
      keywords:
        'Washougal auto broker, used cars Washougal WA, Columbia Gorge delivery, Oregon sales tax savings, Washougal car dealer',
    },
    testimonial: {
      name: 'Ryan Mitchell',
      location: 'Washougal, WA',
      text:
        'Rockhound Auto made buying from Oregon easy for our Washougal family. They found a perfect Toyota 4Runner and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Battle Ground',
    state: 'Washington',
    slug: 'battle-ground',
    distance: '92 miles from Warrenton',
    distanceMiles: 92,
    description:
      'Battle Ground families get transparent auto broker service with coordinated delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'Battle Ground residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout North Clark County.',
    landmarks: ['Battle Ground Lake State Park', 'Lewisville Park', 'Downtown Battle Ground', 'Lucia Falls Park'],
    localKeywords: [
      'Battle Ground auto broker',
      'used cars Battle Ground WA',
      'North Clark County delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Vancouver', slug: 'vancouver' },
      { name: 'Ridgefield', slug: 'ridgefield' },
      { name: 'Woodland', slug: 'woodland' },
      { name: 'Camas', slug: 'camas' },
    ],
    coordinates: { lat: 45.7809, lng: -122.5334 },
    seo: {
      title: 'Battle Ground WA Auto Broker | Oregon Tax Savings',
      description:
        'Battle Ground gets transparent auto broker service with delivery. Buy from Oregon, save on sales tax—expert guidance for North Clark County families.',
      keywords:
        'Battle Ground auto broker, used cars Battle Ground WA, North Clark County delivery, Oregon tax-free vehicles, Battle Ground car dealer',
    },
    testimonial: {
      name: 'Amanda Johnson',
      location: 'Battle Ground, WA',
      text:
        'Rockhound Auto helped us save thousands by buying from Oregon. They found a perfect Chevy Traverse and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Ridgefield',
    state: 'Washington',
    slug: 'ridgefield',
    distance: '88 miles from Warrenton',
    distanceMiles: 88,
    description:
      'Ridgefield customers get expert auto broker service with coordinated delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Ridgefield residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout North Clark County.',
    landmarks: ['Ridgefield National Wildlife Refuge', 'Historic Downtown', 'Abrams Park', 'Overlook Park'],
    localKeywords: [
      'Ridgefield auto broker',
      'used cars Ridgefield WA',
      'North Clark County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Battle Ground', slug: 'battle-ground' },
      { name: 'Vancouver', slug: 'vancouver' },
      { name: 'Portland', slug: 'portland' },
      { name: 'La Center', slug: 'la-center' },
    ],
    coordinates: { lat: 45.8151, lng: -122.7445 },
    seo: {
      title: 'Ridgefield WA Auto Broker | Oregon Tax-Free Vehicles',
      description:
        'Ridgefield gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—transparent pricing for North Clark County communities.',
      keywords:
        'Ridgefield auto broker, used cars Ridgefield WA, North Clark County delivery, Oregon sales tax savings, Ridgefield car dealer',
    },
    testimonial: {
      name: 'David Anderson',
      location: 'Ridgefield, WA',
      text:
        'Rockhound Auto made buying from Oregon seamless for our Ridgefield family. They found a perfect Ford Explorer and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Longview',
    state: 'Washington',
    slug: 'longview',
    distance: '35 miles from Warrenton',
    distanceMiles: 35,
    description:
      'Longview customers get expert auto broker service with free delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Longview residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout Cowlitz County with transparent pricing.',
    landmarks: ['Lake Sacajawea Park', 'Nutty Narrows Bridge', 'Columbia Theatre', 'Mint Valley Golf Course'],
    localKeywords: [
      'Longview auto broker',
      'used cars Longview WA',
      'Cowlitz County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Kelso', slug: 'kelso' },
      { name: 'Cathlamet', slug: 'cathlamet' },
      { name: 'Warrenton', slug: 'warrenton' },
      { name: 'Woodland', slug: 'woodland' },
    ],
    coordinates: { lat: 46.1382, lng: -122.9382 },
    seo: {
      title: 'Longview WA Auto Broker | Free Delivery | Rockhound Auto',
      description:
        'Longview gets expert auto broker service with free delivery. Buy from Oregon, save on sales tax—transparent pricing for Cowlitz County. Just 35 miles from Warrenton.',
      keywords:
        'Longview auto broker, used cars Longview WA, Cowlitz County delivery, Oregon sales tax savings, Longview car dealer',
    },
    testimonial: {
      name: 'Jessica Williams',
      location: 'Longview, WA',
      text:
        'Living in Longview, buying from Oregon saved us thousands. Rockhound Auto found a perfect Nissan Rogue and delivered it free to our home.',
      rating: 5,
    },
  },
  {
    name: 'Kelso',
    state: 'Washington',
    slug: 'kelso',
    distance: '38 miles from Warrenton',
    distanceMiles: 38,
    description:
      'Kelso families get transparent auto broker service with free delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'Kelso residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout Cowlitz County with expert guidance.',
    landmarks: ['Tam O\'Shanter Park', 'Three Rivers Mall', 'Cowlitz River', 'Kelso Boat Launch'],
    localKeywords: [
      'Kelso auto broker',
      'used cars Kelso WA',
      'Cowlitz County delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Longview', slug: 'longview' },
      { name: 'Cathlamet', slug: 'cathlamet' },
      { name: 'Warrenton', slug: 'warrenton' },
      { name: 'Castle Rock', slug: 'castle-rock' },
    ],
    coordinates: { lat: 46.1468, lng: -122.9085 },
    seo: {
      title: 'Kelso WA Auto Broker | Oregon Tax Savings | Free Delivery',
      description:
        'Kelso gets transparent auto broker service with free delivery. Buy from Oregon, save on sales tax—expert guidance for Cowlitz County families.',
      keywords:
        'Kelso auto broker, used cars Kelso WA, Cowlitz County delivery, Oregon tax-free vehicles, Kelso car dealer',
    },
    testimonial: {
      name: 'Robert Taylor',
      location: 'Kelso, WA',
      text:
        'Rockhound Auto helped us save thousands by buying from Oregon. They found a perfect GMC Sierra and delivered it free to Kelso.',
      rating: 5,
    },
  },
  {
    name: 'Olympia',
    state: 'Washington',
    slug: 'olympia',
    distance: '95 miles from Warrenton',
    distanceMiles: 95,
    description:
      'Olympia customers get expert auto broker service with coordinated delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Olympia residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery to the capital city with transparent pricing.',
    landmarks: ['Washington State Capitol', 'Percival Landing', 'Tumwater Falls', 'Hands On Children\'s Museum'],
    localKeywords: [
      'Olympia auto broker',
      'used cars Olympia WA',
      'Thurston County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Lacey', slug: 'lacey' },
      { name: 'Tumwater', slug: 'tumwater' },
      { name: 'Centralia', slug: 'centralia' },
      { name: 'Tacoma', slug: 'tacoma' },
    ],
    coordinates: { lat: 47.0379, lng: -122.9007 },
    seo: {
      title: 'Olympia WA Auto Broker | Capital City Oregon Tax Savings',
      description:
        'Olympia gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—transparent pricing for Thurston County. Better than WA dealerships.',
      keywords:
        'Olympia auto broker, used cars Olympia WA, Thurston County delivery, Oregon sales tax savings, Olympia car dealer',
    },
    testimonial: {
      name: 'Laura Martinez',
      location: 'Olympia, WA',
      text:
        'Working for the state, I needed a reliable commuter car. Rockhound Auto found me a perfect Honda Civic, coordinated delivery to Olympia, and saved me thousands on sales tax.',
      rating: 5,
    },
  },
  {
    name: 'Lacey',
    state: 'Washington',
    slug: 'lacey',
    distance: '98 miles from Warrenton',
    distanceMiles: 98,
    description:
      'Lacey families get transparent auto broker service with coordinated delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'Lacey residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout Thurston County.',
    landmarks: ['Woodland Creek Community Park', 'Lacey Museum', 'Regional Athletic Complex', 'Nisqually Wildlife Refuge'],
    localKeywords: [
      'Lacey auto broker',
      'used cars Lacey WA',
      'Thurston County delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Olympia', slug: 'olympia' },
      { name: 'Tumwater', slug: 'tumwater' },
      { name: 'Centralia', slug: 'centralia' },
      { name: 'Tacoma', slug: 'tacoma' },
    ],
    coordinates: { lat: 47.0343, lng: -122.8232 },
    seo: {
      title: 'Lacey WA Auto Broker | Oregon Tax-Free Vehicle Delivery',
      description:
        'Lacey gets transparent auto broker service with delivery. Buy from Oregon, save on sales tax—expert guidance for Thurston County families.',
      keywords:
        'Lacey auto broker, used cars Lacey WA, Thurston County delivery, Oregon tax-free vehicles, Lacey car dealer',
    },
    testimonial: {
      name: 'Matthew Clark',
      location: 'Lacey, WA',
      text:
        'Rockhound Auto made buying from Oregon easy for our Lacey family. They found a perfect Toyota RAV4 and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Tumwater',
    state: 'Washington',
    slug: 'tumwater',
    distance: '93 miles from Warrenton',
    distanceMiles: 93,
    description:
      'Tumwater customers get expert auto broker service with coordinated delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Tumwater families: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout Thurston County.',
    landmarks: ['Tumwater Falls Park', 'Olympia Brewery', 'Tumwater Historical Park', 'Capitol Lake'],
    localKeywords: [
      'Tumwater auto broker',
      'used cars Tumwater WA',
      'Thurston County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Olympia', slug: 'olympia' },
      { name: 'Lacey', slug: 'lacey' },
      { name: 'Centralia', slug: 'centralia' },
      { name: 'Tacoma', slug: 'tacoma' },
    ],
    coordinates: { lat: 47.0073, lng: -122.9093 },
    seo: {
      title: 'Tumwater WA Auto Broker | Oregon Tax Savings | Delivery',
      description:
        'Tumwater gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—transparent pricing for Thurston County communities.',
      keywords:
        'Tumwater auto broker, used cars Tumwater WA, Thurston County delivery, Oregon sales tax savings, Tumwater car dealer',
    },
    testimonial: {
      name: 'Stephanie Harris',
      location: 'Tumwater, WA',
      text:
        'Rockhound Auto helped us buy from Oregon and save thousands. They found a perfect Mazda CX-5 and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Centralia',
    state: 'Washington',
    slug: 'centralia',
    distance: '65 miles from Warrenton',
    distanceMiles: 65,
    description:
      'Centralia customers get expert auto broker service with free delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Centralia residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout Lewis County with transparent pricing.',
    landmarks: ['Fort Borst Park', 'Centralia Outlets', 'Seminary Hill Natural Area', 'Riverside Golf Course'],
    localKeywords: [
      'Centralia auto broker',
      'used cars Centralia WA',
      'Lewis County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Chehalis', slug: 'chehalis' },
      { name: 'Olympia', slug: 'olympia' },
      { name: 'Longview', slug: 'longview' },
      { name: 'Tumwater', slug: 'tumwater' },
    ],
    coordinates: { lat: 46.7162, lng: -122.9543 },
    seo: {
      title: 'Centralia WA Auto Broker | Free Delivery | Oregon Tax Savings',
      description:
        'Centralia gets expert auto broker service with free delivery. Buy from Oregon, save on sales tax—transparent pricing for Lewis County. Just 65 miles from Warrenton.',
      keywords:
        'Centralia auto broker, used cars Centralia WA, Lewis County delivery, Oregon sales tax savings, Centralia car dealer',
    },
    testimonial: {
      name: 'Daniel Robinson',
      location: 'Centralia, WA',
      text:
        'Rockhound Auto made buying from Oregon seamless. They found a perfect Ford F-250 and delivered it free to Centralia.',
      rating: 5,
    },
  },
  {
    name: 'Chehalis',
    state: 'Washington',
    slug: 'chehalis',
    distance: '68 miles from Warrenton',
    distanceMiles: 68,
    description:
      'Chehalis families get transparent auto broker service with free delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'Chehalis residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout Lewis County with expert guidance.',
    landmarks: ['Stan Hedwall Park', 'Chehalis-Centralia Railroad', 'Recreation Park', 'Veterans Memorial Museum'],
    localKeywords: [
      'Chehalis auto broker',
      'used cars Chehalis WA',
      'Lewis County delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Centralia', slug: 'centralia' },
      { name: 'Olympia', slug: 'olympia' },
      { name: 'Longview', slug: 'longview' },
      { name: 'Morton', slug: 'morton' },
    ],
    coordinates: { lat: 46.6620, lng: -122.9640 },
    seo: {
      title: 'Chehalis WA Auto Broker | Oregon Tax-Free | Free Delivery',
      description:
        'Chehalis gets transparent auto broker service with free delivery. Buy from Oregon, save on sales tax—expert guidance for Lewis County families.',
      keywords:
        'Chehalis auto broker, used cars Chehalis WA, Lewis County delivery, Oregon tax-free vehicles, Chehalis car dealer',
    },
    testimonial: {
      name: 'Karen White',
      location: 'Chehalis, WA',
      text:
        'Rockhound Auto helped us save thousands by buying from Oregon. They found a perfect Dodge Ram and delivered it free to Chehalis.',
      rating: 5,
    },
  },
  {
    name: 'Long Beach',
    state: 'Washington',
    slug: 'long-beach',
    distance: '22 miles from Warrenton',
    distanceMiles: 22,
    description:
      'Long Beach customers get expert auto broker service with free delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Long Beach residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout the Long Beach Peninsula with transparent pricing.',
    landmarks: ['Long Beach Boardwalk', 'World\'s Longest Beach', 'Marsh\'s Free Museum', 'Cape Disappointment'],
    localKeywords: [
      'Long Beach auto broker',
      'used cars Long Beach WA',
      'Long Beach Peninsula delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Ilwaco', slug: 'ilwaco' },
      { name: 'Ocean Park', slug: 'ocean-park' },
      { name: 'Warrenton', slug: 'warrenton' },
      { name: 'Astoria', slug: 'astoria' },
      { name: 'Seaview', slug: 'seaview' },
    ],
    coordinates: { lat: 46.3523, lng: -124.0544 },
    seo: {
      title: 'Long Beach WA Auto Broker | Peninsula Free Delivery',
      description:
        'Long Beach gets expert auto broker service with free delivery. Buy from Oregon, save on sales tax—transparent pricing for Long Beach Peninsula. Just 22 miles from Warrenton.',
      keywords:
        'Long Beach auto broker, used cars Long Beach WA, Long Beach Peninsula delivery, Oregon sales tax savings, Long Beach car dealer',
    },
    testimonial: {
      name: 'Susan Miller',
      location: 'Long Beach, WA',
      text:
        'Living on the peninsula, buying from Oregon saved us thousands. Rockhound Auto found a perfect Subaru Crosstrek and delivered it free to Long Beach.',
      rating: 5,
    },
  },
  {
    name: 'Ilwaco',
    state: 'Washington',
    slug: 'ilwaco',
    distance: '18 miles from Warrenton',
    distanceMiles: 18,
    description:
      'Ilwaco customers get transparent auto broker service with free delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Ilwaco residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout the Long Beach Peninsula.',
    landmarks: ['Cape Disappointment State Park', 'Ilwaco Port', 'Columbia Pacific Heritage Museum', 'North Head Lighthouse'],
    localKeywords: [
      'Ilwaco auto broker',
      'used cars Ilwaco WA',
      'Long Beach Peninsula delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Long Beach', slug: 'long-beach' },
      { name: 'Ocean Park', slug: 'ocean-park' },
      { name: 'Warrenton', slug: 'warrenton' },
      { name: 'Astoria', slug: 'astoria' },
    ],
    coordinates: { lat: 46.3087, lng: -124.0432 },
    seo: {
      title: 'Ilwaco WA Auto Broker | Oregon Tax Savings | Free Delivery',
      description:
        'Ilwaco gets transparent auto broker service with free delivery. Buy from Oregon, save on sales tax—expert guidance for Long Beach Peninsula. Just 18 miles from Warrenton.',
      keywords:
        'Ilwaco auto broker, used cars Ilwaco WA, Long Beach Peninsula delivery, Oregon tax-free vehicles, Ilwaco car dealer',
    },
    testimonial: {
      name: 'Gregory Adams',
      location: 'Ilwaco, WA',
      text:
        'Rockhound Auto made buying from Oregon easy for our Ilwaco family. They found a perfect Toyota Tacoma and delivered it free to our home.',
      rating: 5,
    },
  },
  {
    name: 'Ocean Park',
    state: 'Washington',
    slug: 'ocean-park',
    distance: '28 miles from Warrenton',
    distanceMiles: 28,
    description:
      'Ocean Park families get expert auto broker service with free delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'Ocean Park residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout the Long Beach Peninsula.',
    landmarks: ['Jack\'s Country Store', 'Ocean Park Beach', 'Loomis Lake State Park', 'Willapa Bay'],
    localKeywords: [
      'Ocean Park auto broker',
      'used cars Ocean Park WA',
      'Long Beach Peninsula delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Long Beach', slug: 'long-beach' },
      { name: 'Ilwaco', slug: 'ilwaco' },
      { name: 'Warrenton', slug: 'warrenton' },
      { name: 'Nahcotta', slug: 'nahcotta' },
    ],
    coordinates: { lat: 46.4912, lng: -124.0496 },
    seo: {
      title: 'Ocean Park WA Auto Broker | Peninsula Oregon Tax Savings',
      description:
        'Ocean Park gets expert auto broker service with free delivery. Buy from Oregon, save on sales tax—transparent pricing for Long Beach Peninsula communities.',
      keywords:
        'Ocean Park auto broker, used cars Ocean Park WA, Long Beach Peninsula delivery, Oregon tax-free vehicles, Ocean Park car dealer',
    },
    testimonial: {
      name: 'Patricia Evans',
      location: 'Ocean Park, WA',
      text:
        'Rockhound Auto helped us buy from Oregon and save thousands on sales tax. They found a perfect Honda CR-V and delivered it free to our home.',
      rating: 5,
    },
  },
  {
    name: 'Westport',
    state: 'Washington',
    slug: 'westport',
    distance: '75 miles from Warrenton',
    distanceMiles: 75,
    description:
      'Westport customers get expert auto broker service with coordinated delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Westport residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery to the fishing capital of the Northwest.',
    landmarks: ['Westport Marina', 'Grays Harbor Lighthouse', 'Westhaven State Park', 'Westport Light Trail'],
    localKeywords: [
      'Westport auto broker',
      'used cars Westport WA',
      'Grays Harbor delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Aberdeen', slug: 'aberdeen' },
      { name: 'Hoquiam', slug: 'hoquiam' },
      { name: 'Montesano', slug: 'montesano' },
      { name: 'Ocean Shores', slug: 'ocean-shores' },
    ],
    coordinates: { lat: 46.8890, lng: -124.1051 },
    seo: {
      title: 'Westport WA Auto Broker | Fishing Capital Oregon Tax Savings',
      description:
        'Westport gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—transparent pricing for Grays Harbor coastal communities.',
      keywords:
        'Westport auto broker, used cars Westport WA, Grays Harbor delivery, Oregon sales tax savings, Westport car dealer',
    },
    testimonial: {
      name: 'Charles Baker',
      location: 'Westport, WA',
      text:
        'Rockhound Auto found me a perfect Ford F-150 for towing my fishing boat. They coordinated delivery to Westport, saved me thousands on sales tax, and made it easy.',
      rating: 5,
    },
  },
  {
    name: 'Aberdeen',
    state: 'Washington',
    slug: 'aberdeen',
    distance: '85 miles from Warrenton',
    distanceMiles: 85,
    description:
      'Aberdeen customers get expert auto broker service with coordinated delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Aberdeen residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout Grays Harbor County.',
    landmarks: ['Kurt Cobain Memorial Park', 'Grays Harbor Historical Seaport', 'Morrison Riverfront Park', 'Aberdeen Museum'],
    localKeywords: [
      'Aberdeen auto broker',
      'used cars Aberdeen WA',
      'Grays Harbor County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Hoquiam', slug: 'hoquiam' },
      { name: 'Westport', slug: 'westport' },
      { name: 'Montesano', slug: 'montesano' },
      { name: 'Ocean Shores', slug: 'ocean-shores' },
    ],
    coordinates: { lat: 46.9754, lng: -123.8157 },
    seo: {
      title: 'Aberdeen WA Auto Broker | Grays Harbor Oregon Tax Savings',
      description:
        'Aberdeen gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—transparent pricing for Grays Harbor County communities.',
      keywords:
        'Aberdeen auto broker, used cars Aberdeen WA, Grays Harbor County delivery, Oregon sales tax savings, Aberdeen car dealer',
    },
    testimonial: {
      name: 'Linda Thompson',
      location: 'Aberdeen, WA',
      text:
        'Rockhound Auto made buying from Oregon easy for our Aberdeen family. They found a perfect Chevrolet Equinox and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Hoquiam',
    state: 'Washington',
    slug: 'hoquiam',
    distance: '82 miles from Warrenton',
    distanceMiles: 82,
    description:
      'Hoquiam families get transparent auto broker service with coordinated delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'Hoquiam residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout Grays Harbor County.',
    landmarks: ['Hoquiam Castle', 'Polson Museum', 'Olympic Stadium', 'Hoquiam River'],
    localKeywords: [
      'Hoquiam auto broker',
      'used cars Hoquiam WA',
      'Grays Harbor delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Aberdeen', slug: 'aberdeen' },
      { name: 'Westport', slug: 'westport' },
      { name: 'Montesano', slug: 'montesano' },
      { name: 'Ocean Shores', slug: 'ocean-shores' },
    ],
    coordinates: { lat: 46.9809, lng: -122.5334 },
    seo: {
      title: 'Hoquiam WA Auto Broker | Oregon Tax-Free Vehicle Delivery',
      description:
        'Hoquiam gets transparent auto broker service with delivery. Buy from Oregon, save on sales tax—expert guidance for Grays Harbor County families.',
      keywords:
        'Hoquiam auto broker, used cars Hoquiam WA, Grays Harbor delivery, Oregon tax-free vehicles, Hoquiam car dealer',
    },
    testimonial: {
      name: 'Richard Moore',
      location: 'Hoquiam, WA',
      text:
        'Rockhound Auto helped us buy from Oregon and save thousands on sales tax. They found a perfect GMC Terrain and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Raymond',
    state: 'Washington',
    slug: 'raymond',
    distance: '55 miles from Warrenton',
    distanceMiles: 55,
    description:
      'Raymond customers get expert auto broker service with free delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Raymond residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout Pacific County with transparent pricing.',
    landmarks: ['Northwest Carriage Museum', 'Willapa Seaport Museum', 'Raymond Theatre', 'Willapa River'],
    localKeywords: [
      'Raymond auto broker',
      'used cars Raymond WA',
      'Pacific County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'South Bend', slug: 'south-bend' },
      { name: 'Long Beach', slug: 'long-beach' },
      { name: 'Aberdeen', slug: 'aberdeen' },
      { name: 'Tokeland', slug: 'tokeland' },
    ],
    coordinates: { lat: 46.6862, lng: -123.7329 },
    seo: {
      title: 'Raymond WA Auto Broker | Pacific County Free Delivery',
      description:
        'Raymond gets expert auto broker service with free delivery. Buy from Oregon, save on sales tax—transparent pricing for Pacific County. Just 55 miles from Warrenton.',
      keywords:
        'Raymond auto broker, used cars Raymond WA, Pacific County delivery, Oregon sales tax savings, Raymond car dealer',
    },
    testimonial: {
      name: 'Barbara Scott',
      location: 'Raymond, WA',
      text:
        'Rockhound Auto made buying from Oregon seamless. They found a perfect Jeep Compass and delivered it free to our home.',
      rating: 5,
    },
  },
  {
    name: 'South Bend',
    state: 'Washington',
    slug: 'south-bend',
    distance: '50 miles from Warrenton',
    distanceMiles: 50,
    description:
      'South Bend families get transparent auto broker service with free delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'South Bend residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout Pacific County with expert guidance.',
    landmarks: ['Pacific County Courthouse', 'Willapa Bay', 'South Bend Marina', 'Robert Bush Park'],
    localKeywords: [
      'South Bend auto broker',
      'used cars South Bend WA',
      'Pacific County delivery',
      'Oregon tax-free vehicles',
    ],
    nearbyAreas: [
      { name: 'Raymond', slug: 'raymond' },
      { name: 'Long Beach', slug: 'long-beach' },
      { name: 'Cathlamet', slug: 'cathlamet' },
      { name: 'Naselle', slug: 'naselle' },
    ],
    coordinates: { lat: 46.6629, lng: -123.7951 },
    seo: {
      title: 'South Bend WA Auto Broker | Oregon Tax-Free | Free Delivery',
      description:
        'South Bend gets transparent auto broker service with free delivery. Buy from Oregon, save on sales tax—expert guidance for Pacific County families.',
      keywords:
        'South Bend auto broker, used cars South Bend WA, Pacific County delivery, Oregon tax-free vehicles, South Bend car dealer',
    },
    testimonial: {
      name: 'William Harris',
      location: 'South Bend, WA',
      text:
        'Rockhound Auto helped us save thousands by buying from Oregon. They found a perfect Ford Ranger and delivered it free to South Bend.',
      rating: 5,
    },
  },
  {
    name: 'Cathlamet',
    state: 'Washington',
    slug: 'cathlamet',
    distance: '25 miles from Warrenton',
    distanceMiles: 25,
    description:
      'Cathlamet customers get transparent auto broker service with free delivery. We help Washington customers save on Oregon purchases.',
    heroDescription:
      'Cathlamet residents: Buy from Oregon and avoid Washington sales tax. Free delivery throughout Wahkiakum County with transparent pricing.',
    landmarks: ['Cathlamet Marina', 'Julia Butler Hansen Refuge', 'Elochoman Slough', 'Puget Island'],
    localKeywords: [
      'Cathlamet auto broker',
      'used cars Cathlamet WA',
      'Wahkiakum County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Warrenton', slug: 'warrenton' },
      { name: 'Longview', slug: 'longview' },
      { name: 'Astoria', slug: 'astoria' },
      { name: 'Skamokawa', slug: 'skamokawa' },
    ],
    coordinates: { lat: 46.2043, lng: -123.3832 },
    seo: {
      title: 'Cathlamet WA Auto Broker | Wahkiakum County Free Delivery',
      description:
        'Cathlamet gets transparent auto broker service with free delivery. Buy from Oregon, save on sales tax—transparent pricing for Wahkiakum County. Just 25 miles from Warrenton.',
      keywords:
        'Cathlamet auto broker, used cars Cathlamet WA, Wahkiakum County delivery, Oregon sales tax savings, Cathlamet car dealer',
    },
    testimonial: {
      name: 'Nancy Phillips',
      location: 'Cathlamet, WA',
      text:
        'Living in Cathlamet, buying from Oregon saved us thousands. Rockhound Auto found a perfect Subaru Outback and delivered it free to our home.',
      rating: 5,
    },
  },
  {
    name: 'Seattle',
    state: 'Washington',
    slug: 'seattle',
    distance: '175 miles from Warrenton',
    distanceMiles: 175,
    description:
      'Seattle customers get expert auto broker service with coordinated delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Seattle residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout King County with transparent pricing.',
    landmarks: ['Space Needle', 'Pike Place Market', 'Seattle Waterfront', 'University of Washington'],
    localKeywords: [
      'Seattle auto broker',
      'used cars Seattle WA',
      'King County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Tacoma', slug: 'tacoma' },
      { name: 'Olympia', slug: 'olympia' },
      { name: 'Bellevue', slug: 'bellevue' },
      { name: 'Everett', slug: 'everett' },
    ],
    coordinates: { lat: 47.6062, lng: -122.3321 },
    seo: {
      title: 'Seattle WA Auto Broker | Oregon Tax Savings | King County',
      description:
        'Seattle gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—statewide search, transparent pricing for King County.',
      keywords:
        'Seattle auto broker, used cars Seattle WA, King County delivery, Oregon sales tax savings, Seattle car dealer alternative',
    },
    testimonial: {
      name: 'Andrew Wilson',
      location: 'Seattle, WA',
      text:
        'Living in Seattle, buying from Oregon saved me thousands in sales tax. Rockhound Auto found a perfect Tesla Model Y, coordinated delivery to Capitol Hill, and made it seamless.',
      rating: 5,
    },
  },
  {
    name: 'Tacoma',
    state: 'Washington',
    slug: 'tacoma',
    distance: '140 miles from Warrenton',
    distanceMiles: 140,
    description:
      'Tacoma customers get expert auto broker service with coordinated delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Tacoma residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout Pierce County with transparent pricing.',
    landmarks: ['Point Defiance Park', 'Museum of Glass', 'Tacoma Waterfront', 'Wright Park'],
    localKeywords: [
      'Tacoma auto broker',
      'used cars Tacoma WA',
      'Pierce County delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Seattle', slug: 'seattle' },
      { name: 'Olympia', slug: 'olympia' },
      { name: 'Puyallup', slug: 'puyallup' },
      { name: 'Lakewood', slug: 'lakewood' },
    ],
    coordinates: { lat: 47.2529, lng: -122.4443 },
    seo: {
      title: 'Tacoma WA Auto Broker | Pierce County Oregon Tax Savings',
      description:
        'Tacoma gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—statewide search, transparent pricing for Pierce County.',
      keywords:
        'Tacoma auto broker, used cars Tacoma WA, Pierce County delivery, Oregon sales tax savings, Tacoma car dealer',
    },
    testimonial: {
      name: 'Michelle Rodriguez',
      location: 'Tacoma, WA',
      text:
        'Rockhound Auto made buying from Oregon easy for our Tacoma family. They found a perfect Honda Pilot and delivered it to our home.',
      rating: 5,
    },
  },
  {
    name: 'Spokane',
    state: 'Washington',
    slug: 'spokane',
    distance: '380 miles from Warrenton',
    distanceMiles: 380,
    description:
      'Spokane customers get expert auto broker service with coordinated delivery. We help Washington residents save on Oregon purchases.',
    heroDescription:
      'Spokane residents: Buy from Oregon and avoid Washington sales tax. We coordinate delivery throughout Eastern Washington with transparent pricing.',
    landmarks: ['Riverfront Park', 'Manito Park', 'Spokane Falls', 'Gonzaga University'],
    localKeywords: [
      'Spokane auto broker',
      'used cars Spokane WA',
      'Eastern Washington delivery',
      'Oregon sales tax savings',
    ],
    nearbyAreas: [
      { name: 'Coeur d\'Alene', slug: 'coeur-d-alene' },
      { name: 'Spokane Valley', slug: 'spokane-valley' },
      { name: 'Cheney', slug: 'cheney' },
      { name: 'Liberty Lake', slug: 'liberty-lake' },
    ],
    coordinates: { lat: 47.6588, lng: -117.4260 },
    seo: {
      title: 'Spokane WA Auto Broker | Eastern WA Oregon Tax Savings',
      description:
        'Spokane gets expert auto broker service with delivery. Buy from Oregon, save on sales tax—statewide search, transparent pricing for Eastern Washington.',
      keywords:
        'Spokane auto broker, used cars Spokane WA, Eastern Washington delivery, Oregon sales tax savings, Spokane car dealer',
    },
    testimonial: {
      name: 'Christopher Allen',
      location: 'Spokane, WA',
      text:
        'Rockhound Auto coordinated delivery all the way to Spokane. They found a perfect Ford Explorer and saved us thousands on sales tax.',
      rating: 5,
    },
  },
  {
    name: 'Bend',
    state: 'Oregon',
    slug: 'bend',
    distance: '200 miles from Warrenton',
    distanceMiles: 200,
    description:
      'Central Oregon customers appreciate our outdoor lifestyle vehicle expertise and statewide search capabilities. We understand mountain living vehicle needs.',
    heroDescription:
      'Bend adventurers: We understand your lifestyle needs. From Subarus for skiing to trucks for camping—we find vehicles built for Central Oregon living and coordinate delivery to the high desert.',
    landmarks: ['Mount Bachelor', 'Deschutes River', 'Old Mill District', 'Pilot Butte', 'Smith Rock'],
    localKeywords: [
      'Bend auto broker',
      'Central Oregon delivery',
      'outdoor lifestyle vehicles',
      'mountain living cars',
    ],
    nearbyAreas: [
      { name: 'Eugene', slug: 'eugene' },
      { name: 'Salem', slug: 'salem' },
      { name: 'Portland', slug: 'portland' },
      { name: 'Springfield', slug: 'springfield' },
    ],
    coordinates: { lat: 44.0582, lng: -121.3153 },
    seo: {
      title: 'Bend OR Auto Broker | Outdoor Lifestyle Vehicle Experts',
      description:
        'Bend customers get vehicles built for Central Oregon living. Subarus for skiing, trucks for adventures. Statewide search with delivery to the high desert. Better than Bend dealers.',
      keywords:
        'Bend auto broker, Central Oregon auto delivery, outdoor lifestyle vehicles, mountain living cars, Deschutes County auto broker',
    },
    testimonial: {
      name: 'Jake Morrison',
      location: 'Bend, OR',
      text:
        'Living in Bend, I needed a vehicle for skiing and mountain biking. Rockhound Auto found the perfect Subaru Outback Wilderness, beat Bend dealer prices by $3,400, and delivered it to my door. Perfect!',
      rating: 5,
    },
  },
];

export const getCityBySlug = (slug: string): CityData | undefined => {
  try {
    return serviceAreaCities.find((city) => city.slug === slug);
  } catch {
    return undefined;
  }
};
